<template>
  <div class="tw:py-1 tw:pr-0 tw:pl-4 tw:rounded tw:border-1 tw:border-solid tw:border-gray-200">
    <v-menu v-model="menu" :close-on-content-click="false" location="bottom" transition="none" max-width="300px">

      <template v-slot:activator="{ props }">
        <div class="date-range-selector tw:cursor-pointer" v-bind="props">
          <span v-if="isAllTime">All Time</span>
          <span v-else>{{ startDateFormatted }} &ndash; {{ endDateFormatted }}</span>
          <ChevronDown class="tw:mr-2" :size="20" />
        </div>
      </template>

      <div class="popup tw:border-1 tw:border-solid tw:border-gray-300 tw:shadow-lg tw:drop-shadow-sm tw:w-80">
        <ul :class="quickSelectionClass">
          <li class="h4 tw:text-gray-500">Previous Calendar</li>

          <li class="tw:text-sm tw:py-1 tw:pl-4 last-week" @click="setLastWeek">Week</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 last-month" @click="setLastMonth">Month</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 last-quarter" @click="setLastQuarter">Quarter</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 last-year" @click="setLastYear">Year</li>

          <li class="h4 tw:text-gray-500">Trailing Days</li>

          <li class="tw:text-sm tw:py-1 tw:pl-4 trailing-30" @click="setTrailing(30)">30 Days</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 trailing-90" @click="setTrailing(90)">90 Days</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 trailing-180" @click="setTrailing(180)">180 Days</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 trailing-365" @click="setTrailing(365)">365 Days</li>

          <li class="h4 tw:text-gray-500">Other</li>

          <li class="tw:text-sm tw:py-1 tw:pl-4 all-time" @click="setAllTime">All Time</li>
          <li class="tw:text-sm tw:py-1 tw:pl-4 year-to-date" @click="setYearToDate">Year-To-Date</li>
        </ul>
        <div class="tw:my-4 tw:mx-auto tw:border-0 tw:border-b-1 tw:border-solid tw:border-gray-300"></div>

        <h4 class="tw:text-gray-500">Custom Range</h4>

        <div class="custom">
          <div class="date-range">
            <v-dialog ref="sdialog" v-model="startDateDialog" width="290px">
              <template v-slot:activator="{ props }">
                <v-text-field v-model="startDateTextField" variant="outlined" density="compact" :readonly="true" v-bind="props" data-test="date-range-start-date-activator"></v-text-field>
              </template>

              <v-date-picker scrollable color="secondary" header-color="primary" data-test="date-range-start-date-picker"
                             v-model="selectedStartDate"
                             @click:cancel="selectedStartDate = startDate; startDateDialog = false"
                             @update:modelValue="chooseStartDate" />
            </v-dialog>
            <v-dialog ref="edialog" v-model="endDateDialog" width="290px">
              <template v-slot:activator="{ props }">
                <v-text-field v-model="endDateTextField" variant="outlined" density="compact" :readonly="true" v-bind="props" data-test="date-range-end-date-activator"></v-text-field>
              </template>
              <v-date-picker scrollable color="secondary" header-color="primary" data-test="date-range-end-date-picker"
                             v-model="selectedEndDate"
                             @click:cancel="selectedEndDate = endDate; endDateDialog = false"
                             @update:modelValue="chooseEndDate" />
            </v-dialog>
          </div>
        </div>
        <div class="buttons">
          <v-btn variant="flat" color="primary" @click="applyDateRange" data-test="date-range-apply-button">Apply</v-btn>
          <v-btn variant="outlined" @click="cancelDateRange" data-test="date-range-cancel-button">Cancel</v-btn>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { ChevronDown } from 'lucide-vue-next';
import { VDatePicker} from 'vuetify/components/VDatePicker';

export default {
  name: 'ManagedDateRange',
  components: { VDatePicker, ChevronDown },
  props: {
    initialStartDate: Date,
    initialEndDate: Date,
    onApply: Function
  },
  data: () => ({
    menu: false,
    apply: false,
    startDateDialog: false,
    endDateDialog: false,
    newStartDate: undefined,
    newEndDate: undefined,
    allTimeStart: DateTime.fromObject({ year: 2019, month: 1, day: 1 }).startOf('day'),
    allTimeEnd: DateTime.local().plus({ year: 1 }).endOf('day'),
    selectedStartDate: undefined,
    selectedEndDate: undefined
  }),
  computed: {
    startDate: {
      get() {
        return this.newStartDate || this.initialStartDate;
      },
      set(value) {
        const startDateTime = DateTime.fromJSDate(value).startOf('day');
        const endDateTime = DateTime.fromJSDate(this.endDate).startOf('day');
        if (endDateTime < startDateTime) {
          this.endDate = this.startDate;
        }
        this.newStartDate = value;
      }
    },
    endDate: {
      get() {
        return this.newEndDate || this.initialEndDate;
      },
      set(value) {
        const startDateTime = DateTime.fromISO(this.startDate).startOf('day');
        const endDateTime = DateTime.fromISO(value).startOf('day');
        if (startDateTime > endDateTime) {
          this.startDate = this.endDate;
        }
        this.newEndDate = value;
      }
    },
    startDateFormatted() {
      return DateTime.fromJSDate(this.startDate).toLocaleString(DateTime.DATE_MED);
    },
    endDateFormatted() {
      return DateTime.fromJSDate(this.endDate).toLocaleString(DateTime.DATE_MED);
    },
    startDateTextField() {
      return DateTime.fromJSDate(this.startDate).toLocaleString(DateTime.DATE_SHORT);
    },
    endDateTextField() {
      return DateTime.fromJSDate(this.endDate).toLocaleString(DateTime.DATE_SHORT);
    },
    isAllTime() {
      const endDateTime = DateTime.fromJSDate(this.endDate);
      const startDateTime = DateTime.fromJSDate(this.startDate);
      return startDateTime.hasSame(this.allTimeStart, 'year') && endDateTime.hasSame(this.allTimeEnd, 'year');
    },
    quickSelectionClass() {
      const endDateTime = DateTime.fromISO(this.endDate);
      const startDateTime = DateTime.fromISO(this.startDate);
      const now = DateTime.local();
      if (this.isAllTime) {
        return 'all-time';
      }
      if (endDateTime.startOf('week').hasSame(startDateTime, 'day')) {
        return 'last-week';
      }
      if (endDateTime.startOf('month').hasSame(startDateTime, 'day')) {
        return 'last-month';
      }
      if (endDateTime.startOf('quarter').hasSame(startDateTime, 'day')) {
        return 'last-quarter';
      }
      if (endDateTime.hasSame(now, 'day') && startDateTime.hasSame(now.startOf('year'), 'day')) {
        return 'year-to-date';
      }
      if (endDateTime.startOf('year').hasSame(startDateTime, 'day')) {
        return 'last-year';
      }
      if (endDateTime.minus({ days: 30 }).hasSame(startDateTime, 'day')) {
        return 'trailing-30';
      }
      if (endDateTime.minus({ days: 90 }).hasSame(startDateTime, 'day')) {
        return 'trailing-90';
      }
      if (endDateTime.minus({ days: 180 }).hasSame(startDateTime, 'day')) {
        return 'trailing-180';
      }
      if (endDateTime.minus({ days: 365 }).hasSame(startDateTime, 'day')) {
        return 'trailing-365';
      }
      return '';
    }
  },
  watch: {
    menu() {
      if (this.menu) {
        this.selectedStartDate = this.startDate;
        this.selectedEndDate = this.endDate;
        return;
      }
      // dates were not changed
      if (this.initialStartDate === this.newStartDate && this.initialEndDate === this.newEndDate) {
        return;
      }
      if (this.apply) {
        this.onApply({
          startDate: this.newStartDate || undefined,
          endDate: this.newEndDate || undefined
        });
      } else {
        this.newStartDate = this.initialStartDate;
        this.newEndDate = this.initialEndDate;
      }
    },
    initialStartDate() {
      this.newStartDate = this.initialStartDate;
    },
    initialEndDate() {
      this.newEndDate = this.initialEndDate;
    }
  },
  methods: {
    chooseStartDate() {
      this.startDate = this.selectedStartDate;
      this.startDateDialog = false;
    },
    chooseEndDate() {
      this.endDate = this.selectedEndDate;
      this.endDateDialog = false;
    },
    cancelDateRange() {
      this.apply = false;
      this.menu = false;
    },
    applyDateRange() {
      this.apply = true;
      this.menu = false;
    },
    setLastWeek() {
      const now = DateTime.local();
      this.startDate = now.minus({ week: 1 }).startOf('week').toJSDate();
      this.endDate = now.minus({ week: 1 }).endOf('week').toJSDate();
    },
    setLastMonth() {
      const now = DateTime.local();
      this.startDate = now.minus({ month: 1 }).startOf('month').toJSDate();
      this.endDate = now.minus({ month: 1 }).endOf('month').toJSDate();
    },
    setLastQuarter() {
      const now = DateTime.local();
      this.startDate = now.minus({ quarter: 1 }).startOf('quarter').toJSDate();
      this.endDate = now.minus({ quarter: 1 }).endOf('quarter').toJSDate();
    },
    setLastYear() {
      const now = DateTime.local();
      this.startDate = now.minus({ year: 1 }).startOf('year').toJSDate();
      this.endDate = now.minus({ year: 1 }).endOf('year').toJSDate();
    },
    setYearToDate() {
      const now = DateTime.local();
      this.startDate = now.startOf('year').toJSDate();
      this.endDate = now.endOf('day').toJSDate();
    },
    setAllTime() {
      this.startDate = this.allTimeStart.toJSDate();
      this.endDate = this.allTimeEnd.toJSDate();
    },
    setTrailing(days) {
      const now = DateTime.local();
      this.startDate = now.minus({ days: days + 1 }).toJSDate();
      this.endDate = now.minus({ days: 1 }).toJSDate();
    },
  }
};
</script>

<style scoped>
.date-range-selector {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
}

.date-range-selector > i {
  padding: 0.25rem 0.5rem 0.25rem 0;
}

.date-range-selector > span {
  font-size: 1.2rem;
}

.popup {
  background: white;
}

.popup > h4,
.popup > ul > li.h4 {
  font-weight: 400;
  font-size: 0.8rem;
  text-transform: uppercase;
  /*noinspection CssUnresolvedCustomProperty*/
  padding: 0.5rem 0.5rem 0.25rem 1rem;
}

.popup ul {
  list-style: none;
  padding: 0.5rem 0 0;
}

.popup li:not(.h4) {
  cursor: pointer;
}

.popup li:not(.h4):hover {
  /*noinspection CssUnresolvedCustomProperty*/
  background: var(--v-gray-lighten4);
}

.popup li:not(.h4):before {
  display: inline-block;
  content: '';
  width: 1rem;
}

/*noinspection CssUnusedSymbol*/
.popup ul.last-week > li.last-week:before,
.popup ul.last-month > li.last-month:before,
.popup ul.last-quarter > li.last-quarter:before,
.popup ul.last-year > li.last-year:before,
.popup ul.year-to-date > li.year-to-date:before,
.popup ul.trailing-30 > li.trailing-30:before,
.popup ul.trailing-90 > li.trailing-90:before,
.popup ul.trailing-180 > li.trailing-180:before,
.popup ul.trailing-365 > li.trailing-365:before,
.popup ul.all-time > li.all-time:before {
  content: '✓';
}

.popup .rule {
  margin: 1rem auto;
  /*noinspection CssUnresolvedCustomProperty*/
  border-top: 1px solid var(--v-border-base);
}

.popup .custom {
  padding: 0 1rem;
}

.popup .date-range {
  display: grid;
  grid-template-columns: 1fr 50%;
  grid-column-gap: 1rem;
}

.popup .buttons {
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 0.5rem;
  padding: 1rem;
}
</style>
